// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import GameActivityStore from '@app/features/game_activity/GameActivityStore';
import {VOICE_CHANNEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import styles from '@app/features/user/components/modals/tabs/GameActivityTab.module.css';
import {CompactComboboxRow} from '@app/features/user/components/modals/tabs/components/CompactComboboxRow';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {Slider} from '@app/features/ui/components/Slider';
import VoiceOverlaySettings, {
	type VoiceOverlayAvatarSize,
	type VoiceOverlayVisibility,
} from '@app/features/voice/state/VoiceOverlaySettings';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {HeadphonesIcon, MicrophoneSlashIcon, XIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useState} from 'react';

const VOICE_WIDGET_DESCRIPTOR = msg({
	message: 'Voice widget',
	comment: 'Settings section title for the in-game voice widget.',
});
const AVATAR_SIZE_DESCRIPTOR = msg({
	message: 'Avatar size',
	comment: 'Settings label for the in-game voice widget avatar size.',
});
const DISPLAY_NAMES_DESCRIPTOR = msg({
	message: 'Display names',
	comment: 'Settings label for when the in-game voice widget shows names.',
});
const DISPLAY_USERS_DESCRIPTOR = msg({
	message: 'Display users',
	comment: 'Settings label for when the in-game voice widget shows users.',
});
const MAX_USERS_DESCRIPTOR = msg({
	message: 'Maximum users displayed',
	comment: 'Settings label for how many people the in-game voice widget shows.',
});
const ONLY_WHILE_SPEAKING_DESCRIPTOR = msg({
	message: 'Only while speaking',
	comment: 'Option for showing a voice widget row only while that person is speaking.',
});
const ALWAYS_DESCRIPTOR = msg({
	message: 'Always',
	comment: 'Generic option meaning a setting stays on all the time.',
});
const NEVER_DESCRIPTOR = msg({
	message: 'Never',
	comment: 'Generic option meaning a setting stays off.',
});
const SMALL_DESCRIPTOR = msg({
	message: 'Small',
	comment: 'Size option.',
});
const MEDIUM_DESCRIPTOR = msg({
	message: 'Medium',
	comment: 'Size option.',
});
const LARGE_DESCRIPTOR = msg({
	message: 'Large',
	comment: 'Size option.',
});
const OFF_DESCRIPTOR = msg({
	message: 'Off',
	comment: 'Slider label for a value of zero.',
});

const PREVIEW_USERS = [
	{name: 'a.turgut61', color: '#5865f2', speaking: false, muted: false, deafened: false},
	{name: 'Acetaminophen', color: '#5865f2', speaking: false, muted: true, deafened: false},
	{name: 'Kovanth', color: '#5865f2', speaking: true, muted: false, deafened: false},
	{name: 'Scynder', color: '#5865f2', speaking: false, muted: false, deafened: true},
] as const;

const sizeClass = {
	small: styles.avatarSmall,
	medium: styles.avatarMedium,
	large: styles.avatarLarge,
} as const;

const VoiceWidgetPreview = observer(function VoiceWidgetPreview() {
	const {i18n} = useLingui();
	const visible =
		VoiceOverlaySettings.showUsers === 'never'
			? []
			: PREVIEW_USERS.filter((user) => VoiceOverlaySettings.showUsers !== 'speaking' || user.speaking).slice(
					0,
					VoiceOverlaySettings.maxUsers,
				);
	return (
		<div className={styles.preview} data-flx="user.game-activity-tab.voice-widget-preview">
			<div className={styles.widget}>
				<div className={styles.dashed}>
					{visible.map((user) => {
						const showName =
							VoiceOverlaySettings.showNames === 'always' ||
							(VoiceOverlaySettings.showNames === 'speaking' && user.speaking);
						return (
							<div key={user.name} className={styles.user}>
								<span
									className={clsx(
										styles.avatar,
										sizeClass[VoiceOverlaySettings.avatarSize],
										user.speaking && styles.avatarSpeaking,
									)}
									style={{background: user.color}}
								>
									{user.name.slice(0, 1).toUpperCase()}
								</span>
								{showName ? (
									<span className={styles.name}>
										<span className={styles.nameText}>{user.name}</span>
										{user.muted ? <MicrophoneSlashIcon className={styles.statusIcon} weight="fill" /> : null}
										{user.deafened ? <HeadphonesIcon className={styles.statusIcon} weight="fill" /> : null}
									</span>
								) : null}
							</div>
						);
					})}
				</div>
				<div className={styles.channelRow}>
					<div className={styles.channel}>{i18n._(VOICE_CHANNEL_DESCRIPTOR)}</div>
					<div className={styles.close} aria-hidden>
						<XIcon weight="bold" />
					</div>
				</div>
			</div>
		</div>
	);
});

const GameActivityTab: React.FC = observer(() => {
	const {i18n} = useLingui();
	const [busy, setBusy] = useState(false);
	const visibilityOptions = [
		{value: 'always' as const, label: i18n._(ALWAYS_DESCRIPTOR)},
		{value: 'speaking' as const, label: i18n._(ONLY_WHILE_SPEAKING_DESCRIPTOR)},
		{value: 'never' as const, label: i18n._(NEVER_DESCRIPTOR)},
	];
	const sizeOptions = [
		{value: 'small' as const, label: i18n._(SMALL_DESCRIPTOR)},
		{value: 'medium' as const, label: i18n._(MEDIUM_DESCRIPTOR)},
		{value: 'large' as const, label: i18n._(LARGE_DESCRIPTOR)},
	];

	const handleEnabledChange = async (value: boolean) => {
		setBusy(true);
		try {
			await GameActivityStore.setEnabled(value);
		} finally {
			setBusy(false);
		}
	};

	return (
		<SettingsTabContainer data-flx="user.game-activity-tab.settings-tab-container">
			<SettingsSection
				id="game-activity"
				title={<Trans>Game activity</Trans>}
				data-flx="user.game-activity-tab.settings-section"
			>
				<Switch
					label={<Trans>Share Steam game activity</Trans>}
					description={
						<Trans>
							When enabled, games you launch through Steam can appear on your profile and in member lists.
						</Trans>
					}
					value={GameActivityStore.enabled}
					disabled={busy}
					onChange={(value) => void handleEnabledChange(value)}
					data-flx="user.game-activity-tab.switch.game-activity-enabled"
				/>
			</SettingsSection>
			<SettingsSection
				id="voice-widget"
				title={i18n._(VOICE_WIDGET_DESCRIPTOR)}
				description={
					<Trans>
						Shown on top of other windows only while you are in a voice channel. It closes when you leave, and it
						does not watch the screen or run while you are not in a call.
					</Trans>
				}
				data-flx="user.game-activity-tab.voice-widget-section"
			>
				<VoiceWidgetPreview />
				<CompactComboboxRow<VoiceOverlayAvatarSize>
					label={i18n._(AVATAR_SIZE_DESCRIPTOR)}
					value={VoiceOverlaySettings.avatarSize}
					options={sizeOptions}
					onChange={VoiceOverlaySettings.setAvatarSize}
					isSearchable={false}
					dataFlx="user.game-activity-tab.avatar-size"
				/>
				<CompactComboboxRow<VoiceOverlayVisibility>
					label={i18n._(DISPLAY_NAMES_DESCRIPTOR)}
					value={VoiceOverlaySettings.showNames}
					options={visibilityOptions}
					onChange={VoiceOverlaySettings.setShowNames}
					isSearchable={false}
					dataFlx="user.game-activity-tab.display-names"
				/>
				<CompactComboboxRow<VoiceOverlayVisibility>
					label={i18n._(DISPLAY_USERS_DESCRIPTOR)}
					value={VoiceOverlaySettings.showUsers}
					options={visibilityOptions}
					onChange={VoiceOverlaySettings.setShowUsers}
					isSearchable={false}
					dataFlx="user.game-activity-tab.display-users"
				/>
				<div className={styles.sliderBlock}>
					<div className={styles.sliderLabel}>{i18n._(MAX_USERS_DESCRIPTOR)}</div>
					<Slider
					value={VoiceOverlaySettings.maxUsers}
					defaultValue={VoiceOverlaySettings.maxUsers}
					factoryDefaultValue={4}
					minValue={0}
					maxValue={25}
					step={1}
					markers={[0, 5, 10, 15, 20, 25]}
					markerPosition="below"
					onMarkerRender={(value) => (value === 0 ? i18n._(OFF_DESCRIPTOR) : String(value))}
					onValueChange={(value) => VoiceOverlaySettings.setMaxUsers(value)}
					ariaLabel={i18n._(MAX_USERS_DESCRIPTOR)}
				/>
				</div>
			</SettingsSection>
		</SettingsTabContainer>
	);
});

export default GameActivityTab;
